import React, { useState } from "react";
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from "use-places-autocomplete";
import CuModal from "../../common_component/component/CuModal";

const AddLocationModal = ({ onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    type: "Retail",
    city: "",
    state: "",
    zipCode: "",
    contactNumber: "",
    email: "",
    contactPersonName: "",
    contactPersonNumber: "",
    gstNumber: "",
    operationalHours: "",
    address: "",
    latLng: { lat: null, lng: null },
    partners: [], // For dynamic partner fields
  });

  const [partnerFields, setPartnerFields] = useState([{ name: "", share: "" }]);

  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: { country: "in" },
    },
    debounce: 300,
  });

  const handleInput = (e) => {
    setValue(e.target.value);
  };

  const handleSelect = async (address) => {
    setValue(address, false);
    clearSuggestions();

    try {
      const results = await getGeocode({ address });
      const { lat, lng } = await getLatLng(results[0]);
      setFormData({
        ...formData,
        address,
        latLng: { lat, lng },
      });
    } catch (error) {
      console.error("Error fetching geocode: ", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handlePartnerChange = (index, e) => {
    const { name, value } = e.target;
    const newPartners = [...partnerFields];
    newPartners[index][name] = value;
    setPartnerFields(newPartners);
  };

  const addPartnerField = () => {
    setPartnerFields([...partnerFields, { name: "", share: "" }]);
  };

  const removePartnerField = (index) => {
    const newPartners = partnerFields.filter((_, i) => i !== index);
    setPartnerFields(newPartners);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (
      !formData.name ||
      !formData.type ||
      !formData.city ||
      !formData.state ||
      !formData.zipCode ||
      !formData.contactNumber ||
      !formData.email ||
      !formData.contactPersonName ||
      !formData.contactPersonNumber ||
      !formData.gstNumber ||
      !formData.operationalHours ||
      !formData.address
    ) {
      alert("All fields are required.");
      return;
    }

    console.log("New Location Added:", {
      ...formData,
      partners: partnerFields,
    }); // Replace with API integration
    onClose();
  };

  return (
    <CuModal>
      <div className="bg-white p-6 w-full h-full overflow-auto">
        <h2 className="text-3xl font-semibold text-gray-800 mb-6">
          Add New Pharmacy Location
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Pharmacy Details */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pharmacy Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="border border-gray-300 rounded px-4 py-2 w-full"
                placeholder="Enter Pharmacy Name"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="border border-gray-300 rounded px-4 py-2 w-full"
              >
                <option value="Retail">Retail</option>
                <option value="Wholesale">Wholesale</option>
              </select>
            </div>
            {/* Contact Details */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Person Name
              </label>
              <input
                type="text"
                name="contactPersonName"
                value={formData.contactPersonName}
                onChange={handleChange}
                className="border border-gray-300 rounded px-4 py-2 w-full"
                placeholder="Enter Contact Person Name"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Person Number
              </label>
              <input
                type="tel"
                name="contactPersonNumber"
                value={formData.contactPersonNumber}
                onChange={handleChange}
                className="border border-gray-300 rounded px-4 py-2 w-full"
                placeholder="Enter Contact Person Number"
                required
              />
            </div>
            {/* Additional Fields */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <input
                type="text"
                value={value}
                onChange={handleInput}
                className="border border-gray-300 rounded px-4 py-2 w-full"
                placeholder="Search Address"
                disabled={!ready}
              />
              {status === "OK" && (
                <ul className="border border-gray-300 rounded mt-2">
                  {data.map(({ place_id, description }) => (
                    <li
                      key={place_id}
                      className="p-2 cursor-pointer hover:bg-gray-200"
                      onClick={() => handleSelect(description)}
                    >
                      {description}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          {/* Partner Sharing Section */}
          <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-4">
            Partner Sharing
          </h3>
          {partnerFields.map((partner, index) => (
            <div
              key={index}
              className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center mb-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Partner Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={partner.name}
                  onChange={(e) => handlePartnerChange(index, e)}
                  className="border border-gray-300 rounded px-4 py-2 w-full"
                  placeholder="Enter Partner Name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Share (%)
                </label>
                <input
                  type="number"
                  name="share"
                  value={partner.share}
                  onChange={(e) => handlePartnerChange(index, e)}
                  className="border border-gray-300 rounded px-4 py-2 w-full"
                  placeholder="Enter Share Percentage"
                />
              </div>
              <div className="col-span-2 flex justify-end">
                <button
                  type="button"
                  className="text-red-500 hover:underline"
                  onClick={() => removePartnerField(index)}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            className="text-green-500 hover:underline"
            onClick={addPartnerField}
          >
            Add Partner
          </button>
          {/* Submit */}
          <div className="flex justify-end mt-6">
            <button
              type="button"
              className="bg-gray-300 text-gray-700 px-4 py-2 rounded mr-2"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-green-500 text-white px-4 py-2 rounded"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </CuModal>
  );
};

export default AddLocationModal;
